import { X } from "lucide-react"
import { useEffect, useState } from "react"
import type { Client, ClientContact } from "../types"

type AddClientModalProps = {
  isOpen: boolean
  onClose: () => void
  onSave: (client: Omit<Client, "id" | "createdAt" | "lastContacted">) => void
  client?: Client | null
  onDelete?: (clientId: string) => void
}

const AddClientModal = ({ isOpen, onClose, onSave, client, onDelete }: AddClientModalProps) => {
  const isEditMode = !!client
  const [formData, setFormData] = useState({
    name: client?.name || "",
    company: client?.company || "",
    status: (client?.status || "Lead") as Client["status"],
    email: client?.email || "",
    phone: client?.phone || "",
    address: client?.address || "",
    website: client?.website || "",
    tags: client?.tags || [],
    primaryContactName: client?.contacts?.find((c) => c.isPrimary)?.name || "",
    primaryContactRole: client?.contacts?.find((c) => c.isPrimary)?.role || "",
    primaryContactEmail: client?.contacts?.find((c) => c.isPrimary)?.email || "",
    primaryContactPhone: client?.contacts?.find((c) => c.isPrimary)?.phone || "",
  })

  const [tagInput, setTagInput] = useState("")

  // Reset form when client changes
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (client) {
        setFormData({
          name: client.name || "",
          company: client.company || "",
          status: client.status || "Lead",
          email: client.email || "",
          phone: client.phone || "",
          address: client.address || "",
          website: client.website || "",
          tags: client.tags || [],
          primaryContactName: client.contacts?.find((c) => c.isPrimary)?.name || "",
          primaryContactRole: client.contacts?.find((c) => c.isPrimary)?.role || "",
          primaryContactEmail: client.contacts?.find((c) => c.isPrimary)?.email || "",
          primaryContactPhone: client.contacts?.find((c) => c.isPrimary)?.phone || "",
        })
      } else {
        setFormData({
          name: "",
          company: "",
          status: "Lead",
          email: "",
          phone: "",
          address: "",
          website: "",
          tags: [],
          primaryContactName: "",
          primaryContactRole: "",
          primaryContactEmail: "",
          primaryContactPhone: "",
        })
      }
    }, 0)
    return () => window.clearTimeout(timer)
  }, [client])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const primaryContact: ClientContact = {
      id: `contact-${crypto.randomUUID()}`,
      name: formData.primaryContactName || formData.name,
      role: formData.primaryContactRole || "Primary Contact",
      email: formData.primaryContactEmail || formData.email,
      phone: formData.primaryContactPhone || formData.phone,
      isPrimary: true,
    }

    const newClient: Omit<Client, "id" | "createdAt" | "lastContacted"> = {
      name: formData.name,
      company: formData.company,
      status: formData.status,
      tags: formData.tags,
      contacts: [primaryContact],
      email: formData.email,
      phone: formData.phone,
      address: formData.address || undefined,
      website: formData.website || undefined,
      lifetimeValue: "$0",
      monthlyRecurring: "$0",
      outstandingBalance: "$0",
      totalInvoiced: "$0",
      totalPaid: "$0",
      projects: [],
      activity: [],
      notes: [],
      files: [],
    }

    onSave(newClient)
    handleClose()
  }

  const handleClose = () => {
    setFormData({
      name: "",
      company: "",
      status: "Lead",
      email: "",
      phone: "",
      address: "",
      website: "",
      tags: [],
      primaryContactName: "",
      primaryContactRole: "",
      primaryContactEmail: "",
      primaryContactPhone: "",
    })
    setTagInput("")
    onClose()
  }

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }))
      setTagInput("")
    }
  }

  const handleRemoveTag = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }))
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-2xl rounded-[24px] border border-line bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line p-6">
          <h2 className="text-xl font-semibold text-slate-900">
            {isEditMode ? "Edit Client" : "Add New Client"}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-stone hover:bg-slate-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="max-h-[70vh] overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Basic Info */}
            <div>
              <h3 className="mb-4 text-sm font-semibold text-slate-900">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-700">
                    Client Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                    placeholder="Northwind Digital"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-700">
                    Company *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.company}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, company: e.target.value }))
                    }
                    className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                    placeholder="Northwind"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-700">Status *</label>
                  <select
                    required
                    value={formData.status}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, status: e.target.value as Client["status"] }))
                    }
                    className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                  >
                    <option value="Lead">Lead</option>
                    <option value="Active">Active</option>
                    <option value="Past">Past</option>
                    <option value="On Hold">On Hold</option>
                    <option value="Paused">Paused</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-700">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                    className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                    placeholder="contact@company.com"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-700">Phone *</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                    className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-700">Website</label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData((prev) => ({ ...prev, website: e.target.value }))}
                    className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                    placeholder="https://company.com"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="mb-1.5 block text-xs font-medium text-slate-700">Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
                  className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                  placeholder="123 Main St, City, State ZIP"
                />
              </div>
            </div>

            {/* Primary Contact */}
            <div>
              <h3 className="mb-4 text-sm font-semibold text-slate-900">Primary Contact</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-700">Name</label>
                  <input
                    type="text"
                    value={formData.primaryContactName}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, primaryContactName: e.target.value }))
                    }
                    className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                    placeholder="Leave blank to use client name"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-700">Role</label>
                  <input
                    type="text"
                    value={formData.primaryContactRole}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, primaryContactRole: e.target.value }))
                    }
                    className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                    placeholder="CMO, CEO, etc."
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-700">Email</label>
                  <input
                    type="email"
                    value={formData.primaryContactEmail}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, primaryContactEmail: e.target.value }))
                    }
                    className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                    placeholder="Leave blank to use client email"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-700">Phone</label>
                  <input
                    type="tel"
                    value={formData.primaryContactPhone}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, primaryContactPhone: e.target.value }))
                    }
                    className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                    placeholder="Leave blank to use client phone"
                  />
                </div>
              </div>
            </div>

            {/* Tags */}
            <div>
              <h3 className="mb-4 text-sm font-semibold text-slate-900">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    className="flex items-center gap-1.5 rounded-full bg-moss/10 px-3 py-1 text-xs font-medium text-moss"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-moss/70"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      handleAddTag()
                    }
                  }}
                  className="flex-1 rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                  placeholder="Add tag (press Enter)"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="rounded-lg border border-line bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 flex items-center justify-between border-t border-line pt-6">
            <div>
              {isEditMode && onDelete && (
                <button
                  type="button"
                  onClick={() => {
                    if (client && confirm("Are you sure you want to delete this client?")) {
                      onDelete(client.id)
                      handleClose()
                    }
                  }}
                  className="rounded-lg border border-rose-200 bg-white px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50"
                >
                  Delete Client
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-lg border border-line bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-lg bg-moss px-4 py-2 text-sm font-medium text-white hover:bg-moss/90"
              >
                {isEditMode ? "Save Changes" : "Create Client"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddClientModal
